import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@proptr/api-client';
import { useAuth } from '@proptr/api-client/react';
import {
  AlertIcon,
  ArrowIcon,
  AuthShell,
} from '../components/layout/AuthShell.js';
import { client } from '../main.js';

interface OtpRouteState {
  otpToken?: string;
  maskedEmail?: string;
  email?: string;
}

export function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const state = (location.state as OtpRouteState | null) ?? {};

  // Landing here without coming through /login means we have no otpToken to
  // verify against — bounce back.
  useEffect(() => {
    if (!state.otpToken) navigate('/login', { replace: true });
  }, [state.otpToken, navigate]);

  const [digits, setDigits] = useState<string[]>(() => Array(6).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const code = digits.join('');
  const complete = code.length === 6 && /^\d{6}$/.test(code);

  function setDigit(i: number, v: string) {
    const cleaned = v.replace(/\D/g, '');
    setError(null);
    if (cleaned.length > 1) {
      // Pasted several digits — spread them from the focused cell onward.
      const next = [...digits];
      for (let k = 0; k < cleaned.length && i + k < 6; k++) {
        next[i + k] = cleaned[k] ?? '';
      }
      setDigits(next);
      inputs.current[Math.min(i + cleaned.length, 5)]?.focus();
      return;
    }
    const next = [...digits];
    next[i] = cleaned;
    setDigits(next);
    if (cleaned && i < 5) inputs.current[i + 1]?.focus();
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = '';
      setDigits(next);
      inputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) inputs.current[i + 1]?.focus();
  }

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!complete || !state.otpToken) return;
    setSubmitting(true);
    setError(null);
    try {
      await client.auth.verifyAdminOtp({ otpToken: state.otpToken, code });
      // Pull the now-authenticated session into the AuthProvider so
      // RequireAdmin sees the user immediately.
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(messageFor(err));
      setDigits(Array(6).fill(''));
      inputs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-submit the moment the sixth digit lands.
  useEffect(() => {
    if (complete && !submitting) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  return (
    <AuthShell
      eyebrow="Two-step verification"
      headline={
        <>
          One more step.
          <br />
          <em>Prove it’s you.</em>
        </>
      }
      description={
        <>
          <strong>A single-use code is on its way.</strong> It expires in ten
          minutes, and five wrong attempts will retire it.
        </>
      }
    >
      <form className="auth-form" noValidate onSubmit={(e) => void submit(e)}>
        <h1>Verify</h1>
        <p className="auth-subtitle">
          We sent a 6-digit code to{' '}
          <strong>{state.maskedEmail ?? 'your inbox'}</strong>.
        </p>

        {error && (
          <div className="auth-banner" role="alert">
            <AlertIcon />
            <p>{error}</p>
          </div>
        )}

        <label htmlFor="code-0">Verification code</label>
        <div
          className="auth-code-row"
          onPaste={(e) => {
            const text = e.clipboardData.getData('text').trim();
            if (/^\d{6}$/.test(text)) {
              e.preventDefault();
              setDigits(text.split(''));
              inputs.current[5]?.focus();
            }
          }}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              id={`code-${i}`}
              ref={(el) => (inputs.current[i] = el)}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${i + 1}`}
              maxLength={1}
              value={d}
              disabled={submitting}
              autoFocus={i === 0}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onFocus={(e) => e.currentTarget.select()}
            />
          ))}
        </div>

        <button
          type="submit"
          className="auth-primary-button auth-login-button"
          disabled={!complete || submitting}
          aria-busy={submitting}
        >
          {submitting ? (
            <>
              <span className="auth-spinner" aria-hidden="true" /> Verifying
            </>
          ) : (
            <>
              Verify <ArrowIcon />
            </>
          )}
        </button>

        <div className="auth-alt-action">
          <Link to="/login">Use a different email</Link>
          {state.email && (
            <button
              type="button"
              onClick={() => {
                // The password is no longer in memory, so a fresh code means
                // signing in again — bounce to /login carrying the email.
                navigate('/login', {
                  replace: true,
                  state: { email: state.email },
                });
              }}
            >
              Resend code
            </button>
          )}
        </div>
      </form>
    </AuthShell>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.message?.toLowerCase().includes('too many')) {
      return 'Too many attempts. Request a new code.';
    }
    return err.message || 'Verification failed.';
  }
  return (err as Error)?.message || 'Verification failed.';
}
